import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Trash, PencilSimple, FloppyDisk, UploadSimple } from '@phosphor-icons/react';
import { API } from './adminConstants';

export function FooterDocumentManagement() {
  const { getAuthHeader } = useAuth();
  const [docs, setDocs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [form, setForm] = useState({ label: '', slug: '', pdf_url: '', order: 0 });
  const [uploading, setUploading] = useState(false);

  const load = async () => { try { const r = await axios.get(`${API}/admin/footer-documents`, { headers: getAuthHeader() }); setDocs(r.data); } catch { toast.error('Failed'); } };
  useEffect(() => { load(); }, []);
  const resetForm = () => { setForm({ label: '', slug: '', pdf_url: '', order: docs.length }); setEditDoc(null); setShowForm(false); };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return; setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try { const res = await axios.post(`${API}/admin/footer-documents/upload-pdf`, fd, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }); setForm(prev => ({ ...prev, pdf_url: res.data.pdf_url })); toast.success('PDF uploaded'); } catch { toast.error('Upload failed'); }
    setUploading(false); e.target.value = '';
  };

  const handleSave = async () => {
    if (!form.label) { toast.error('Label required'); return; }
    const payload = { ...form, slug: form.slug || form.label.toLowerCase().replace(/[^a-z0-9]+/g, '-') };
    try {
      if (editDoc) { await axios.put(`${API}/admin/footer-documents/${editDoc.id}`, payload, { headers: getAuthHeader() }); toast.success('Updated'); }
      else { await axios.post(`${API}/admin/footer-documents`, payload, { headers: getAuthHeader() }); toast.success('Created'); }
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (d) => { setEditDoc(d); setForm({ label: d.label || '', slug: d.slug || '', pdf_url: d.pdf_url || '', order: d.order || 0 }); setShowForm(true); };
  const handleDelete = async (id) => { try { await axios.delete(`${API}/admin/footer-documents/${id}`, { headers: getAuthHeader() }); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } };

  return (
    <div data-testid="footer-docs-admin">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-outfit font-bold text-2xl text-slate-900">Footer Documents</h1><p className="text-slate-500 text-sm">Manage footer links and upload PDF documents for each item</p></div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-lime-600 hover:bg-lime-700" data-testid="create-footer-doc-btn"><Plus size={15} className="mr-1" />Add Document</Button>
      </div>
      {showForm && (
        <div className="mb-6 bg-white rounded-xl border border-slate-200 p-6" data-testid="footer-doc-form">
          <h3 className="font-semibold text-slate-900 mb-4">{editDoc ? 'Edit Document' : 'New Document'}</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><Label className="text-xs">Display Label *</Label><Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="e.g. Privacy Policy" data-testid="footer-doc-label" /></div>
              <div><Label className="text-xs">Slug (auto)</Label><Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="privacy-policy" /></div>
              <div><Label className="text-xs">Order</Label><Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} min="0" /></div>
            </div>
            <div><Label className="text-xs">PDF Document</Label>
              <div className="flex gap-3 items-center"><div className="flex-1"><Input value={form.pdf_url} onChange={e => setForm({ ...form, pdf_url: e.target.value })} placeholder="PDF URL (auto-filled on upload)" readOnly className="bg-slate-50" /></div>
                <label className="cursor-pointer shrink-0"><Button variant="outline" size="sm" asChild disabled={uploading}><span><UploadSimple size={15} className="mr-1" />{uploading ? 'Uploading...' : 'Upload PDF'}</span></Button><input type="file" accept=".pdf,application/pdf" className="hidden" onChange={handlePdfUpload} data-testid="footer-doc-upload" /></label>
              </div>
              {form.pdf_url && <p className="text-[11px] text-lime-600 mt-1">PDF uploaded: {form.pdf_url}</p>}
            </div>
          </div>
          <div className="flex gap-3 mt-5"><Button variant="outline" onClick={resetForm}>Cancel</Button><Button onClick={handleSave} className="bg-lime-600 hover:bg-lime-700" data-testid="save-footer-doc-btn"><FloppyDisk size={15} className="mr-1" />{editDoc ? 'Update' : 'Create'}</Button></div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Order</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Label</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Slug</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">PDF</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.map(d => (
              <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`footer-doc-row-${d.id}`}>
                <td className="px-4 py-3 text-sm text-slate-500">{d.order}</td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">{d.label}</td>
                <td className="px-4 py-3 text-sm text-slate-500 font-mono text-xs">{d.slug}</td>
                <td className="px-4 py-3">{d.pdf_url ? <span className="text-[10px] px-2 py-0.5 bg-lime-100 text-lime-700 rounded">Uploaded</span> : <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-500 rounded">No PDF</span>}</td>
                <td className="px-4 py-3"><div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(d)} className="h-7 w-7 p-0"><PencilSimple size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(d.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700"><Trash size={14} /></Button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {docs.length === 0 && <div className="p-8 text-center text-slate-500">No footer documents configured</div>}
      </div>
    </div>
  );
}
